import React, { useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '../components/LanguageContext';
import { useAuth } from '../firebase/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Image as ImageIcon } from 'lucide-react';

async function adminApi(getToken, path) {
  const token = await getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(path, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const text = await res.text();
    let err; try { err = JSON.parse(text)?.error; } catch { /* ignore */ }
    throw new Error(err || `HTTP ${res.status}`);
  }
  return res.json();
}

export default function AdminUserGenerations() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user: currentUser, loading: isLoadingMe, getAuthToken } = useAuth();

  useEffect(() => {
    if (isLoadingMe) return;
    if (!currentUser) { navigate('/login', { replace: true }); return; }
    if (currentUser.role === undefined) return;
    if (currentUser.role !== 'admin') { navigate('/dashboard', { replace: true }); }
  }, [currentUser, isLoadingMe, navigate]);

  const {
    data,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ['adminUserGenerations', id],
    queryFn: () => adminApi(getAuthToken, `/api/admin/users/${id}/generations?limit=200`),
    enabled: !!currentUser && currentUser.role === 'admin' && !!id,
  });

  if (isLoadingMe) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-300">
        {language === 'he' ? 'טוען...' : 'Loading...'}
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return null;
  }

  const user = data?.user;
  const generations = Array.isArray(data?.generations) ? data.generations : [];

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="border-slate-700 text-slate-200 hover:bg-slate-800"
              onClick={() => navigate('/admin')}
            >
              <ArrowLeft className="w-4 h-4 me-2" />
              {language === 'he' ? 'חזרה' : 'Back'}
            </Button>

            <div>
              <h1 className="text-2xl font-bold text-white">
                {language === 'he' ? 'תמונות משתמש' : 'User Images'}
              </h1>
              <p className="text-slate-400 text-sm">
                {user ? `${user.name || '-'} • ${user.email}` : (language === 'he' ? 'טוען משתמש...' : 'Loading user...')}
              </p>
            </div>
          </div>

          <div className="text-slate-300 text-sm">
            {language === 'he' ? 'סה"כ:' : 'Total:'} <span className="text-white font-semibold">{generations.length}</span>
          </div>
        </div>

        <Card className="glass-card border-slate-800">
          <CardHeader>
            <CardTitle className="text-white text-xl flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-violet-400" />
              {language === 'he' ? 'גלריה' : 'Gallery'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {error ? (
              <div className="text-red-400">
                {error?.message || (language === 'he' ? 'שגיאה' : 'Error')}
              </div>
            ) : isLoading || isFetching ? (
              <div className="text-slate-400">
                {language === 'he' ? 'טוען תמונות...' : 'Loading images...'}
              </div>
            ) : generations.length === 0 ? (
              <div className="text-slate-400">
                {language === 'he' ? 'אין תמונות למשתמש הזה' : 'No images for this user'}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {generations.map((g) => (
                  <div key={g.id} className="rounded-xl overflow-hidden border border-slate-800 bg-slate-950">
                    <a href={g.generated_image_url} target="_blank" rel="noreferrer">
                      <img
                        src={g.generated_image_url}
                        alt={g.character_name || 'Generated'}
                        className="w-full aspect-square object-cover"
                        loading="lazy"
                      />
                    </a>
                    <div className="p-3">
                      <div className="text-white text-sm font-medium">
                        {g.character_name || '-'}
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        {g.created_at ? new Date(g.created_at).toLocaleString() : '-'}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {language === 'he' ? 'איכות:' : 'Quality:'} {g.quality || '-'}
                        {'  '}|{'  '}
                        {language === 'he' ? 'עלות:' : 'Cost:'} {Number.isFinite(g.credits_spent) ? g.credits_spent : '-'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 text-sm text-slate-500">
          <Link to="/admin" className="text-violet-400 hover:text-violet-300">
            {language === 'he' ? 'חזרה לפאנל ניהול' : 'Back to Admin'}
          </Link>
        </div>
      </div>
    </div>
  );
}
